import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle2, Send, Lock, ShieldCheck, Building2 } from 'lucide-react'
import { authApi } from 'lib/api'
import { Input } from 'components/ui/Input'
import { Button } from 'components/ui/Button'

export function ForgotPassword() {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [cooldown, setCooldown] = useState(0)

  // Check if email is valid format
  const isValidEmail = email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const hasEmail = email.length > 0

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    setMessage('')
    try {
      const lang = (i18n.language || 'en').split('-')[0]
      const res = await authApi.requestPasswordReset(email, lang)
      setMessage(res.data.message || t('auth.resetEmailSent', 'A reset link was sent to this email.'))
      setSubmittedEmail(email)
      setCooldown(30)
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setError(t('auth.noAccountForEmail', 'No account found for this email.'))
      } else {
        setError(e?.response?.data?.error || t('auth.resetRequestFailed', 'Failed to request password reset'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Countdown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [cooldown])

  const maskedEmail = useMemo(() => {
    if (!submittedEmail) return ''
    const [user, domain] = submittedEmail.split('@')
    if (!user || !domain) return submittedEmail
    const visible = user.slice(0, 2)
    return `${visible}${'*'.repeat(Math.max(1, user.length - 2))}@${domain}`
  }, [submittedEmail])

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted relative">
      {/* Subtle background gradient */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5" />
      {/* Soft grid pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="forgot-grid" width="28" height="28" patternUnits="userSpaceOnUse">
              <path d="M 28 0 L 0 0 0 28" fill="none" stroke="currentColor" className="text-foreground" strokeWidth="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#forgot-grid)" />
        </svg>
      </div>
      <div className="w-full max-w-md relative">
        {/* Brand header */}
        <div className="mb-6 flex items-center justify-center gap-2 text-foreground">
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
            <Building2 className="h-5 w-5" />
          </div>
          <span className="text-lg font-semibold tracking-tight">Selektia</span>
        </div>
        {/* Main card with animated border */}
        <div className={`bg-background border rounded-2xl p-6 sm:p-8 shadow-sm transition-all duration-300 ${
          isValidEmail ? 'border-primary/30' : 'border-border'
        }`}>
          {/* Animated icon container */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div className="mx-auto h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Lock className="h-7 w-7" />
              </div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
            {t('auth.forgotPassword')}
          </h1>
          {!submittedEmail ? (
            <p className={`text-sm text-center transition-colors duration-300 ${
              isValidEmail ? 'text-primary' : 'text-muted-foreground'
            }`}>
              {t('auth.forgotPasswordSubtitle')}
            </p>
          ) : (
            <p className="text-sm text-center text-muted-foreground">
              {t('auth.checkInbox')}
            </p>
          )}

          {/* Success message with animation */}
          {submittedEmail && (
            <div className="mt-6 animate-in slide-in-from-top-4 duration-300">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold">{t('auth.emailSent', 'Reset link sent')}</div>
                  <div className="mt-0.5">
                    {t('auth.sentTo', 'We sent the link to')} <span className="font-medium">{maskedEmail}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error message with animation */}
          {error && (
            <div className="mt-6 animate-in slide-in-from-top-4 duration-300">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {!submittedEmail ? (
            <form onSubmit={onSubmit} className="space-y-5 mt-8">
              {/* Divider */}
              <div className="h-px bg-border" />
              {/* Email input with enhanced styling */}
              <div className="relative group">
                <div className={`absolute inset-0 rounded-lg transition-all duration-300 ${
                  isValidEmail 
                    ? 'bg-primary/5 scale-105' 
                    : hasEmail 
                      ? 'bg-primary/2 scale-102' 
                      : ''
                }`} />
              <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 transition-all duration-300">
                    {isValidEmail ? (
                    <CheckCircle2 className={`h-5 w-5 text-primary animate-in zoom-in duration-300`} />
                    ) : (
                      <Mail className={`h-5 w-5 transition-colors duration-300 ${
                        hasEmail ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                    )}
                  </div>
                  <Input
                    type="email"
                  placeholder={t('login.emailPlaceholder')}
                    value={email}
                  onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`pl-12 pr-4 h-12 text-base transition-all duration-300 ${
                      isValidEmail 
                        ? 'border-primary/50 focus:border-primary focus:ring-primary/20' 
                        : hasEmail 
                          ? 'border-primary/30' 
                          : ''
                    }`}
                  />
                </div>
                
                {/* Progress indicator */}
                {hasEmail && (
                  <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 rounded-full ${
                        isValidEmail 
                          ? 'w-full bg-gradient-to-r from-primary to-blue-500' 
                          : 'w-1/2 bg-primary/30'
                      }`}
                    />
                  </div>
                )}

                {/* Helper text */}
                {hasEmail && !isValidEmail && (
                  <p className="mt-2 text-xs text-muted-foreground animate-in fade-in duration-200">
                    Please enter a valid email address
                  </p>
                )}
              </div>

              {/* Submit button with enhanced styling */}
              <Button 
                type="submit" 
                className={`w-full h-12 text-base font-semibold transition-all duration-300 ${
                  isValidEmail && !submitting
                    ? 'bg-gradient-to-r from-primary to-blue-500 hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02]' 
                    : ''
                }`}
                disabled={submitting || !isValidEmail}
              >
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span>{t('common.sending')}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Send className="h-5 w-5" />
                    <span>{t('auth.sendResetLink')}</span>
                  </div>
                )}
              </Button>
              <div className="text-[11px] text-center text-muted-foreground">{t('common.pressEnter')}</div>

              {/* Small reassurance row */}
              <div className="mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>{t('auth.securityNote')}</span>
              </div>
            </form>
          ) : (
            <div className="mt-8 space-y-4">
              <div className="h-px bg-border" />
              <div className="text-center text-sm text-muted-foreground">
                {t('auth.didntReceive')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="h-11"
                  onClick={() => {
                    setSubmittedEmail('')
                    setMessage('')
                    setError('')
                    setEmail('')
                    setCooldown(0)
                  }}
                >
                  {t('auth.changeEmail')}
                </Button>
                <Button
                  type="button"
                  className={`h-11 ${cooldown === 0 ? '' : 'cursor-not-allowed opacity-80'}`}
                  disabled={cooldown !== 0}
                  onClick={async () => {
                    try {
                      setSubmitting(true)
                      const lang = (i18n.language || 'en').split('-')[0]
                      await authApi.requestPasswordReset(submittedEmail, lang)
                      setCooldown(30)
                    } catch (e: any) {
                      if (e?.response?.status === 404) {
                        setError(t('auth.noAccountForEmail', 'No account found for this email.'))
                        setSubmittedEmail('')
                      }
                    } finally {
                      setSubmitting(false)
                    }
                  }}
                >
                  {cooldown === 0 
                    ? t('auth.resendEmail') 
                    : t('auth.resendIn', { s: cooldown })}
                </Button>
              </div>
              <div className="text-xs text-center text-muted-foreground">
                {t('auth.resendHint')}
              </div>
            </div>
          )}

          {/* Back to login link */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 group"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
              <span>{t('auth.backToLogin')}</span>
            </Link>
          </div>
        </div>

        {/* Page footer */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          {t('auth.needHelp')}{' '}
          <a href="mailto:support@selektia.io" className="underline hover:text-foreground">
            {t('auth.contactSupport')}
          </a>
        </div>
        {/* End single-card layout for professional look */}
      </div>
    </div>
  )
}

export default ForgotPassword


